////////////////////////////////////////////
// DEEPLINK STORE — SESSION + PENDING ACTION, PERSISTED ACROSS THE APP SWITCH
////////////////////////////////////////////
//
// A DEEPLINK FLOW LEAVES THE PAGE (WALLET APP OPENS, THEN REDIRECTS BACK INTO
// THE NATIVE BROWSER — OFTEN A FRESH DOCUMENT), SO EVERYTHING THE RETURN TRIP
// NEEDS LIVES IN localStorage: THE ESTABLISHED SESSION (WHO'S CONNECTED, THE
// SHARED SECRET) AND THE ONE IN-FLIGHT ACTION (WHAT TO FINISH ON RETURN).
// REACT READS THE SESSION VIA useSyncExternalStore SO CONNECT STATE IS LIVE.
import type { SolNet } from "@/config/network";

export type DlProvider = "phantom" | "solflare";

// ESTABLISHED WALLET-APP SESSION — EVERYTHING A SIGN REQUEST NEEDS
export interface DlSession {
  provider: DlProvider;
  // SOLANA NET THE SESSION WAS OPENED ON — INFORMATIONAL (ACCOUNT MENU PILL)
  net: SolNet;
  pubkey: string;
  session: string;
  sharedSecret: string;
  dappPublicKey: string;
}

// THE ONE IN-FLIGHT ACTION — WHAT THE RETURN TRIP RESUMES. tab IS STAMPED BY
// savePending SO THE RETURN CAN TELL WHETHER IT LANDED BACK IN THE
// ORIGINATING TAB OR IN A FRESH ONE THE OS OPENED FOR THE REDIRECT.
export type DlPending = (
  | { kind: "connect"; provider: DlProvider; net: SolNet; dappSecretKey: string; dappPublicKey: string; then?: "signin"; at: number }
  | { kind: "signin"; provider: DlProvider; wallet: string; nonce: string; at: number }
  | { kind: "tx"; provider: DlProvider; net: string; action: string; ok: string; successPath?: string; at: number }
) & { tab?: string };

// OUTCOME HANDED FROM A FOREIGN RETURN TAB BACK TO THE ORIGINATING TAB —
// THE FOREIGN TAB PROCESSES THE RESPONSE, WRITES THIS, AND CLOSES ITSELF
export interface DlHandoff {
  toast: { text: string; cls: "ok" | "err" | "info" };
  redirect?: string;
  at: number;
}

// PENDING + HANDOFF ARE PER-TAB (KEY SUFFIXED WITH THE TAB ID) SO FLOWS
// STARTED FROM TWO TABS CAN'T STOMP EACH OTHER — A SECOND TAB OVERWRITING A
// SHARED PENDING WOULD SILENTLY DROP THE FIRST TAB'S SIGNED TRANSACTION
const SESSION_KEY = "fuze:dl:session";
const PENDING_PREFIX = "fuze:dl:pending:";
const HINT_KEY = "fuze:dl:hint";
const HANDOFF_PREFIX = "fuze:dl:handoff:";
const TAB_KEY = "fuze:dl:tab";

// PENDING OLDER THAN THIS IS ABANDONED. GENEROUS ON PURPOSE: A LATE RETURN
// STILL GETS PROCESSED AND THE SERVER GIVES AN HONEST ANSWER (E.G. EXPIRED
// BLOCKHASH) INSTEAD OF THE SIGNED RESPONSE BEING SILENTLY DROPPED.
const PENDING_TTL_MS = 30 * 60 * 1000;

// A HANDOFF IS CONSUMED WITHIN MOMENTS OF THE RETURN — ANYTHING OLDER IS STALE
const HANDOFF_TTL_MS = 2 * 60 * 1000;

////////////////////////////////////////////
// SESSION — CACHED SNAPSHOT FOR useSyncExternalStore
////////////////////////////////////////////

let cached: DlSession | null = null;
let cachedRaw: string | null = null;
const listeners = new Set<() => void>();

function read(key: string): string | null {

  try { return localStorage.getItem(key); } catch { return null; }
}

function write(key: string, value: string | null) {

  try {

    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch { /* STORAGE BLOCKED — DEEPLINK FLOWS JUST WON'T PERSIST */ }
}

function emit() {

  for (const l of listeners) l();
}

export function dlSession(): DlSession | null {

  const raw = read(SESSION_KEY);
  if (raw === cachedRaw)
    return cached;
  cachedRaw = raw;
  try {
    cached = raw ? (JSON.parse(raw) as DlSession) : null;
  } catch {
    cached = null;
  }
  return cached;
}

export function saveDlSession(s: DlSession) {

  write(SESSION_KEY, JSON.stringify(s));
  emit();
}

export function clearDlSession() {

  if (read(SESSION_KEY) === null)
    return;
  write(SESSION_KEY, null);
  emit();
}

// SUBSCRIBE — ALSO CATCHES CROSS-TAB CHANGES VIA THE storage EVENT
export function subscribeDl(cb: () => void): () => void {

  listeners.add(cb);
  const onStorage = (e: StorageEvent) => {

    if (e.key === SESSION_KEY || e.key === null) cb();
  };
  window.addEventListener("storage", onStorage);
  return () => {

    listeners.delete(cb);
    window.removeEventListener("storage", onStorage);
  };
}

////////////////////////////////////////////
// TAB IDENTITY — sessionStorage IS PER-TAB, SO THIS MARKER SURVIVES THE APP
// SWITCH IN THE ORIGINATING TAB BUT DOESN'T EXIST IN A FRESH REDIRECT TAB
////////////////////////////////////////////

export function tabId(): string {

  try {

    let id = sessionStorage.getItem(TAB_KEY);
    if (!id) {

      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(TAB_KEY, id);
    }
    return id;
  } catch {
    return "no-session-storage";
  }
}

////////////////////////////////////////////
// PENDING — THIS TAB'S ONE ACTION AWAITING ITS RETURN TRIP. A FOREIGN
// RETURN TAB READS THE ORIGINATING TAB'S RECORD VIA THE ?dlt= PARAM.
////////////////////////////////////////////

export function savePending(p: DlPending) {

  write(PENDING_PREFIX + tabId(), JSON.stringify({ ...p, tab: tabId() }));
}

export function peekPending(tab?: string): DlPending | null {

  const key = PENDING_PREFIX + (tab ?? tabId());
  const raw = read(key);
  if (!raw)
    return null;
  try {

    const p = JSON.parse(raw) as DlPending;
    // ABANDONED FLOW — TREAT AS GONE (AND CLEAN IT UP)
    if (Date.now() - p.at > PENDING_TTL_MS) {

      write(key, null);
      return null;
    }
    return p;
  } catch {

    write(key, null);
    return null;
  }
}

// TAKE — READ AND CLEAR IN ONE STEP (A RETURN TRIP RESUMES EXACTLY ONCE)
export function takePending(tab?: string): DlPending | null {

  const p = peekPending(tab);
  write(PENDING_PREFIX + (tab ?? tabId()), null);
  return p;
}

////////////////////////////////////////////
// HANDOFF — OUTCOME PARKED FOR A SPECIFIC TAB (SET BY A FOREIGN RETURN TAB
// FOR THE ORIGINATOR JUST BEFORE CLOSING, OR BY A SAME-TAB REDIRECT FOR ITS
// OWN NEXT LOAD; CONSUMED BY THAT TAB ONLY — NEVER STOLEN BY ANOTHER)
////////////////////////////////////////////

export function setHandoff(h: Omit<DlHandoff, "at">, tab: string) {

  write(HANDOFF_PREFIX + tab, JSON.stringify({ ...h, at: Date.now() }));
}

export function peekHandoff(tab?: string): DlHandoff | null {

  const key = HANDOFF_PREFIX + (tab ?? tabId());
  const raw = read(key);
  if (!raw)
    return null;
  try {

    const h = JSON.parse(raw) as DlHandoff;
    if (Date.now() - h.at > HANDOFF_TTL_MS) {

      write(key, null);
      return null;
    }
    return h;
  } catch {

    write(key, null);
    return null;
  }
}

export function takeHandoff(tab?: string): DlHandoff | null {

  const h = peekHandoff(tab);
  write(HANDOFF_PREFIX + (tab ?? tabId()), null);
  return h;
}

////////////////////////////////////////////
// SWEEP — PER-TAB KEYS OUTLIVE CLOSED TABS; DROP EXPIRED ONES AT BOOT
////////////////////////////////////////////

export function sweepDl() {

  try {

    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {

      const key = localStorage.key(i);
      if (!key)
        continue;
      const pending = key.startsWith(PENDING_PREFIX);
      if (!pending && !key.startsWith(HANDOFF_PREFIX))
        continue;
      try {

        const at = (JSON.parse(localStorage.getItem(key) || "") as { at?: number }).at ?? 0;
        if (now - at > (pending ? PENDING_TTL_MS : HANDOFF_TTL_MS))
          localStorage.removeItem(key);
      } catch {
        localStorage.removeItem(key);
      }
    }
  } catch { /* STORAGE BLOCKED — NOTHING TO SWEEP */ }
}

////////////////////////////////////////////
// SIGN-IN HINT — "CONNECTED, NOW TAP AGAIN TO SIGN" AFTER A CONNECT-FOR-LOGIN
////////////////////////////////////////////

export function setSignInHint(provider: DlProvider) {

  write(HINT_KEY, provider);
}

export function takeSignInHint(): DlProvider | null {

  const v = read(HINT_KEY) as DlProvider | null;
  write(HINT_KEY, null);
  return v;
}
